import React from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Clock } from "lucide-react";
import { useLang, RESERVE_URL } from "../context/LanguageContext";

export default function Location() {
  const { t } = useLang();

  return (
    <section id="location" data-testid="location-section" className="relative bg-[#050505] py-24 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 flex flex-col"
          >
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFCC00] mb-6">
              / 06 — {t.location.overline}
            </div>
            <h2 data-testid="location-title" className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.92] mb-6">
              {t.location.title}
            </h2>
            <p className="text-base md:text-lg text-white/70 font-body mb-10 max-w-md">
              {t.location.desc}
            </p>

            <div className="space-y-5 mb-10">
              <ContactRow icon={MapPin} lines={[t.location.address_line1, t.location.address_line2]} />
              <ContactRow icon={Mail} lines={[t.location.email]} href={`mailto:${t.location.email}`} />
              <ContactRow icon={Clock} lines={[t.location.hours]} />
            </div>

            <a
              href={RESERVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-cta"
              className="inline-flex items-center justify-center gap-3 px-8 py-5 bg-[#FFCC00] text-black font-bold text-xs tracking-[0.25em] uppercase hover:bg-white transition-all self-start"
            >
              {t.nav.cta}
              <span className="inline-block w-6 h-px bg-current" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="md:col-span-7 min-h-[420px] md:min-h-[560px] relative overflow-hidden border border-white/10"
            data-testid="location-map"
          >
            <iframe
              title="XPRO Fitness Vidreres map"
              src="https://www.google.com/maps?q=Carrer+Sils+17+Vidreres+Girona&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.95)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
            />
            {/* Pin overlay */}
            <div className="pointer-events-none absolute top-6 left-6 px-4 py-3 bg-black/90 border border-[#FFCC00]/50 flex items-center gap-3">
              <div className="w-2 h-2 bg-[#FFCC00] rounded-full animate-pulse" />
              <span className="text-[11px] tracking-[0.25em] uppercase font-bold text-white">
                XPRO Vidreres
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, lines, href }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <div className="w-11 h-11 border border-white/15 flex items-center justify-center shrink-0 group-hover:border-[#FFCC00] transition-colors">
        <Icon size={16} className="text-[#FFCC00]" />
      </div>
      <div className="space-y-0.5">
        {lines.map((l, i) => (
          <div key={i} className="text-sm md:text-base text-white/85">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:translate-x-1 transition-transform">
      {content}
    </a>
  ) : (
    content
  );
}
